import { create } from 'zustand';
import { BUILT_IN_PRESETS } from '@/core/project/preset';
import {
  COUNTDOWN_SECONDS,
  outOfRoom,
  RecordingClock,
  recordingName,
  TakeLog,
  SilenceWatch,
  storageBudget,
  usesCamera,
  usesScreen,
  type CaptureProblem,
  type NotesSpeed,
  type RecordingMode,
  type StorageBudget,
} from '@/core/recording/session';
import { engine } from '@/engine/client';
import { AudioMixer } from '@/lib/recording/audio-mixer';
import { encoderReady, warmUpVideoEncoder } from '@/lib/recording/encoder-warmup';
import {
  CaptureError,
  captureScreen,
  captureSupport,
  createCaptureController,
  listDevices,
  openCamera,
  openMicrophone,
  stopStream,
  type DeviceList,
} from '@/lib/recording/capture';
import type { Recorder } from '@/lib/recording/recorder';
import { RecorderError } from '@/lib/recording/recorder-error';
import { findLeftovers, freeSpace, keepPersistent, RecordingSession, type LeftoverRecording } from '@/lib/recording/sessions';
import { readPreference, writePreference } from '@/lib/preferences';
import { useEditorStore, type RecordingOrigin } from './editor-store';

/** The recorder carries the MP4 muxer: loaded with the recording setup, not with the home page. */
const loadRecorder = () => import('@/lib/recording/recorder');

/**
 * Screen and camera recording, from the setup in the home page's recording band
 * to handing the finished recording to the editor. Media handles (streams, the
 * recorder, the audio mix) live outside the state: they are resources, not data.
 */

export type RecordingPhase =
  /** Choosing what to record; microphone and camera previews may be running. */
  | { readonly status: 'setup' }
  /** The browser's screen picker is open. */
  | { readonly status: 'choosing' }
  | { readonly status: 'countdown'; readonly remaining: number }
  | { readonly status: 'recording' }
  | { readonly status: 'paused' }
  /** Throwing away the current take: recording goes on, and this countdown is cut too. */
  | { readonly status: 'retaking'; readonly remaining: number; readonly take: number }
  /** Writing the file and preparing it for the editor. */
  | { readonly status: 'saving'; readonly progress: number; readonly reason: StopReason };

export type StopReason = 'user' | 'screen-ended' | 'storage-full' | 'failed';

export type RecordingProblem =
  | { readonly kind: 'capture'; readonly source: CaptureError['source']; readonly problem: CaptureProblem }
  | { readonly kind: 'no-encoder' | 'storage-full' | 'prepare-failed' | 'unknown' };

export interface RecordingOptions {
  readonly microphone: boolean;
  readonly systemAudio: boolean;
  readonly microphoneId: string | null;
  readonly cameraId: string | null;
}

interface RecordingState {
  readonly phase: RecordingPhase;
  readonly mode: RecordingMode;
  readonly options: RecordingOptions;
  readonly devices: DeviceList;
  /** Live camera for the setup and recording previews. */
  readonly cameraStream: MediaStream | null;
  /** The shared screen, for the in-app preview while recording. */
  readonly screenStream: MediaStream | null;
  /** Device trouble shown next to its control; recording can still go ahead without it. */
  readonly deviceProblems: Partial<Record<'microphone' | 'camera', CaptureProblem>>;
  /** Something that stopped a recording from starting or finishing. */
  readonly problem: RecordingProblem | null;
  /** The microphone has been quiet for a while (muted, or the wrong one). */
  readonly micSilent: boolean;
  readonly storage: StorageBudget;
  /** Whether the floating controls window is open. */
  readonly floating: boolean;
  readonly leftovers: readonly LeftoverRecording[];
  /** Speaker notes shown while recording (teleprompter), kept on this device. */
  readonly notes: string;
  readonly notesSpeed: NotesSpeed;
  /** A leftover being repaired or opened, with its progress. */
  readonly recovering: { readonly id: string; readonly progress: number } | null;
}

interface RecordingActions {
  /** The recording band opened: start the previews the chosen options need. */
  activate(): Promise<void>;
  /** The band closed: turn every device off. */
  deactivate(): void;
  setMode(mode: RecordingMode): Promise<void>;
  setMicrophone(on: boolean): Promise<void>;
  setMicrophoneDevice(id: string): Promise<void>;
  setCameraDevice(id: string): Promise<void>;
  setSystemAudio(on: boolean): void;
  setNotes(notes: string): void;
  setNotesSpeed(speed: NotesSpeed): void;
  /** Must be called from a click: opens the screen picker, counts down, records. */
  start(): Promise<void>;
  togglePause(): void;
  /** Throws the current take away and counts down into the next one, without stopping. */
  retake(): Promise<void>;
  /** Changed one's mind during the retake countdown: the take is kept. */
  keepTake(): void;
  stop(reason?: StopReason): Promise<void>;
  /** Throws the recording away (asked to confirm first in the UI). */
  discard(): Promise<void>;
  /** Opens the floating controls window; must be called from a click. */
  popOut(): Promise<void>;
  dismissProblem(): void;
  refreshLeftovers(): Promise<void>;
  recover(id: string): Promise<void>;
  deleteLeftover(id: string): Promise<void>;
}

export type RecordingStore = RecordingState & RecordingActions;

/** The floating controls window (Document Picture-in-Picture), provided by the UI layer. */
export interface FloatingHost {
  readonly supported: boolean;
  open(): Promise<boolean>;
  close(): void;
}

let floatingHost: FloatingHost = { supported: false, open: async () => false, close: () => undefined };

/** Registered once by the UI, so this store needn't know how the window is drawn. */
export function setFloatingHost(host: FloatingHost): void {
  floatingHost = host;
}

const RECORDING_PRESET = BUILT_IN_PRESETS.find((preset) => preset.builtIn === 'recording')!;

/** Live media and timers of the current session. */
const live: {
  mixer: AudioMixer | null;
  microphone: MediaStream | null;
  session: RecordingSession | null;
  recorder: Recorder | null;
  clock: RecordingClock;
  takes: TakeLog;
  freeAtStart: number | null;
  timers: number[];
  silence: SilenceWatch;
  /** Bumped by every setup change; slow device requests that lost the race are dropped. */
  generation: number;
} = {
  mixer: null,
  microphone: null,
  session: null,
  recorder: null,
  clock: new RecordingClock(),
  takes: new TakeLog(),
  freeAtStart: null,
  timers: [],
  silence: new SilenceWatch(),
  generation: 0,
};

const storedMode = readPreference('recording-mode') as RecordingMode | null;

function initialMode(): RecordingMode {
  const support = captureSupport();
  if (!support.screen) return 'camera';
  return storedMode === 'screen' || storedMode === 'screen-camera' || storedMode === 'camera' ? storedMode : 'screen-camera';
}

const initialOptions = (): RecordingOptions => ({
  microphone: readPreference('recording-microphone') !== 'off',
  systemAudio: readPreference('recording-system-audio') !== 'off',
  microphoneId: readPreference('recording-microphone-id'),
  cameraId: readPreference('recording-camera-id'),
});

const mixer = () => (live.mixer ??= new AudioMixer());

function clearTimers(): void {
  for (const timer of live.timers) window.clearInterval(timer);
  live.timers = [];
}

/** Seconds recorded so far (read by the UI on its own tick, not stored). */
export const recordingElapsed = (): number => live.clock.elapsed(performance.now());
/** Bytes recorded so far. */
export const recordingSize = (): number => live.recorder?.size ?? 0;
/** Microphone loudness on a 0..1 meter. */
export const microphoneLevel = (): number => live.mixer?.level('microphone') ?? 0;
/** Tab or system sound loudness on a 0..1 meter. */
export const systemLevel = (): number => live.mixer?.level('system') ?? 0;

const toProblem = (error: unknown): RecordingProblem => {
  if (error instanceof CaptureError) return { kind: 'capture', source: error.source, problem: error.problem };
  if (error instanceof RecorderError && error.reason === 'no-encoder') return { kind: 'no-encoder' };
  if (error instanceof Error && error.name === 'QuotaExceededError') return { kind: 'storage-full' };
  return { kind: 'unknown' };
};

export const useRecordingStore = create<RecordingStore>()((set, get) => {
  const busy = () => get().phase.status !== 'setup';

  async function refreshDevices(): Promise<void> {
    set({ devices: await listDevices() });
  }

  async function startMicrophone(): Promise<void> {
    const generation = ++live.generation;
    stopStream(live.microphone);
    live.microphone = null;
    mixer().remove('microphone');
    const { options } = get();
    if (!options.microphone) return set((state) => ({ deviceProblems: { ...state.deviceProblems, microphone: undefined }, micSilent: false }));
    try {
      const stream = await openMicrophone(options.microphoneId ?? undefined);
      if (generation !== live.generation || !get().options.microphone) return stopStream(stream);
      live.microphone = stream;
      mixer().set('microphone', stream);
      live.silence.reset();
      set((state) => ({ deviceProblems: { ...state.deviceProblems, microphone: undefined } }));
      void refreshDevices();
    } catch (error) {
      // A remembered device that is gone: fall back to the default one once.
      if (error instanceof CaptureError && error.problem === 'not-found' && options.microphoneId) {
        writePreference('recording-microphone-id', null);
        set({ options: { ...get().options, microphoneId: null } });
        return startMicrophone();
      }
      set((state) => ({ deviceProblems: { ...state.deviceProblems, microphone: error instanceof CaptureError ? error.problem : 'unknown' } }));
    }
  }

  async function startCamera(): Promise<void> {
    const generation = ++live.generation;
    stopStream(get().cameraStream);
    set({ cameraStream: null });
    const { mode, options } = get();
    if (!usesCamera(mode)) return set((state) => ({ deviceProblems: { ...state.deviceProblems, camera: undefined } }));
    try {
      const stream = await openCamera(options.cameraId ?? undefined);
      if (generation !== live.generation || !usesCamera(get().mode)) return stopStream(stream);
      set((state) => ({ cameraStream: stream, deviceProblems: { ...state.deviceProblems, camera: undefined } }));
      void refreshDevices();
    } catch (error) {
      if (error instanceof CaptureError && error.problem === 'not-found' && options.cameraId) {
        writePreference('recording-camera-id', null);
        set({ options: { ...get().options, cameraId: null } });
        return startCamera();
      }
      set((state) => ({ deviceProblems: { ...state.deviceProblems, camera: error instanceof CaptureError ? error.problem : 'unknown' } }));
    }
  }

  async function measureStorage(): Promise<void> {
    set({ storage: storageBudget(await freeSpace(), get().mode) });
  }

  /** Watches the microphone level, at a calm pace that is cheap even in a background tab. */
  function watchMicrophone(): void {
    live.timers.push(
      window.setInterval(() => {
        const silent = live.microphone !== null && live.silence.feed(microphoneLevel(), performance.now());
        if (silent !== get().micSilent) set({ micSilent: silent });
      }, 250),
    );
  }

  function releaseDevices(): void {
    clearTimers();
    stopStream(live.microphone);
    live.microphone = null;
    stopStream(get().cameraStream);
    stopStream(get().screenStream);
    live.mixer?.close();
    live.mixer = null;
    set({ cameraStream: null, screenStream: null, micSilent: false });
  }

  /** Hands a prepared recording to the editor, which then owns the session's lifetime. */
  async function openInEditor(session: RecordingSession, screen: File, camera: File | null): Promise<void> {
    const origin: RecordingOrigin = {
      id: session.durable ? session.id : null,
      camera,
      cuts: await session.cuts(),
      release: (keep) => (keep ? session.release() : void session.remove()),
      exported: () => void session.markExported(),
    };
    await useEditorStore.getState().openFile(screen, { preset: RECORDING_PRESET, recording: origin });
  }

  /** Writes out what was recorded and opens it; on trouble the recording stays on the device. */
  async function finish(reason: StopReason): Promise<void> {
    const { recorder, session } = live;
    if (!recorder || !session) return;
    live.recorder = null;
    clearTimers();
    floatingHost.close();
    // Finishing during a retake countdown drops the unfinished take as well.
    if (get().phase.status === 'retaking') live.takes.retake(recordingElapsed());
    live.clock.pause(performance.now());
    await session.saveCuts(live.takes.cuts);
    set({ phase: { status: 'saving', progress: 0, reason }, floating: false });
    // A recording that couldn't be closed cleanly is repaired like one left by a crash.
    const clean = reason !== 'failed' && (await recorder.stop().then(() => true, () => false));
    if (!clean) await recorder.cancel();
    releaseDevices();
    try {
      const raw = await session.raw();
      if (!raw || raw.size === 0) throw new Error('Nothing was recorded');
      const task = engine.prepareRecording(raw, { directory: session.path, name: session.name, salvage: !clean }, (progress) =>
        set({ phase: { status: 'saving', progress: progress.ratio, reason } }),
      );
      const prepared = await task.promise;
      await session.removeRaw();
      live.session = null;
      set({ phase: { status: 'saving', progress: 1, reason } });
      // The saving screen stays up until the editor has the video, then setup is ready for next time.
      await openInEditor(session, prepared.screen, prepared.camera);
      set({ phase: { status: 'setup' }, problem: null });
    } catch {
      // Kept for "recover" on the home page; this tab lets go of it so it is offered there.
      session.release();
      live.session = null;
      set({ phase: { status: 'setup' }, problem: { kind: 'prepare-failed' } });
      void get().refreshLeftovers();
    }
  }

  return {
    phase: { status: 'setup' },
    mode: initialMode(),
    options: initialOptions(),
    devices: { microphones: [], cameras: [] },
    cameraStream: null,
    screenStream: null,
    deviceProblems: {},
    problem: null,
    micSilent: false,
    storage: { verdict: 'ok', minutes: null },
    floating: false,
    leftovers: [],
    recovering: null,
    notes: readPreference('recording-notes') ?? '',
    notesSpeed: (['slow', 'normal', 'fast'] as const).find((speed) => speed === readPreference('recording-notes-speed')) ?? 'normal',

    setNotes(notes) {
      writePreference('recording-notes', notes.trim() ? notes : null);
      set({ notes });
    },

    setNotesSpeed(notesSpeed) {
      writePreference('recording-notes-speed', notesSpeed);
      set({ notesSpeed });
    },

    async activate() {
      if (busy()) return;
      void warmUpVideoEncoder();
      void loadRecorder();
      watchMicrophone();
      navigator.mediaDevices?.addEventListener?.('devicechange', refreshDevices);
      await Promise.all([startMicrophone(), startCamera(), measureStorage()]);
    },

    deactivate() {
      if (busy()) return;
      live.generation++;
      navigator.mediaDevices?.removeEventListener?.('devicechange', refreshDevices);
      releaseDevices();
      set({ deviceProblems: {}, problem: null });
    },

    async setMode(mode) {
      if (busy() || mode === get().mode) return;
      writePreference('recording-mode', mode);
      set({ mode, problem: null });
      await Promise.all([startCamera(), measureStorage()]);
    },

    async setMicrophone(on) {
      writePreference('recording-microphone', on ? 'on' : 'off');
      set((state) => ({ options: { ...state.options, microphone: on } }));
      await startMicrophone();
    },

    async setMicrophoneDevice(id) {
      writePreference('recording-microphone-id', id);
      set((state) => ({ options: { ...state.options, microphoneId: id, microphone: true } }));
      await startMicrophone();
    },

    async setCameraDevice(id) {
      writePreference('recording-camera-id', id);
      set((state) => ({ options: { ...state.options, cameraId: id } }));
      await startCamera();
    },

    setSystemAudio(on) {
      writePreference('recording-system-audio', on ? 'on' : 'off');
      set((state) => ({ options: { ...state.options, systemAudio: on } }));
    },

    async start() {
      if (busy()) return;
      const { mode, options } = get();
      set({ problem: null });
      if (get().storage.verdict === 'full') return set({ problem: { kind: 'storage-full' } });
      await mixer().resume();

      let screen: MediaStream | null = null;
      if (usesScreen(mode)) {
        set({ phase: { status: 'choosing' } });
        const controller = createCaptureController();
        try {
          screen = await captureScreen(options.systemAudio, controller);
        } catch (error) {
          const problem = toProblem(error);
          const cancelled = problem.kind === 'capture' && problem.problem === 'cancelled';
          return set({ phase: { status: 'setup' }, problem: cancelled ? null : problem });
        }
        // Decided right away (Chrome only allows it now): with floating controls the person goes
        // to what they share; without them they stay here, where the controls are.
        const canFloat = floatingHost.supported && (navigator.userActivation?.isActive ?? false);
        try {
          controller?.setFocusBehavior?.(canFloat ? 'focus-captured-surface' : 'no-focus-change');
        } catch {
          // Whole-screen shares have no focus behaviour to set.
        }
        if (canFloat) void floatingHost.open().then((open) => set({ floating: open }));
      }

      const camera = usesCamera(mode) ? get().cameraStream : null;
      const cameraTrack = camera?.getVideoTracks()[0] ?? null;
      if (mode === 'camera' && !cameraTrack) {
        stopStream(screen);
        return set({ phase: { status: 'setup' }, problem: { kind: 'capture', source: 'camera', problem: get().deviceProblems.camera ?? 'not-found' } });
      }
      const screenTrack = screen?.getVideoTracks()[0] ?? null;
      if (screen && screen.getAudioTracks().length > 0) mixer().set('system', screen);
      set({ screenStream: screen });

      // Stopping the share from the browser's own bar ends the recording normally.
      screenTrack?.addEventListener('ended', () => void get().stop('screen-ended'));

      try {
        const session = await RecordingSession.create(recordingName(new Date()));
        live.session = session;
        live.freeAtStart = await freeSpace();
        void keepPersistent();
        // The countdown doubles as the time the encoder needs to be ready; it holds at 1 until it is.
        const recorderModule = loadRecorder();
        const ready = Promise.all([encoderReady(), recorderModule]);
        for (let remaining = COUNTDOWN_SECONDS; remaining > 0; remaining--) {
          set({ phase: { status: 'countdown', remaining } });
          await Promise.all([new Promise((resolve) => window.setTimeout(resolve, 1000)), remaining === 1 ? ready : null]);
          // Cancelled during the countdown (discard, or the share was stopped).
          if (get().phase.status !== 'countdown') return;
        }
        const audio = (live.mixer?.track ?? null) as MediaStreamAudioTrack | null;
        const { Recorder } = await recorderModule;
        const recorder = await Recorder.start({ screen: screenTrack as MediaStreamVideoTrack | null, camera: cameraTrack as MediaStreamVideoTrack | null, audio }, await session.openSink());
        // Cancelled while the recorder was starting: never let it run unseen.
        if (get().phase.status !== 'countdown' || live.session !== session) {
          await recorder.cancel();
          return;
        }
        live.recorder = recorder;
        live.clock.start(performance.now());
        live.takes = new TakeLog();
        set({ phase: { status: 'recording' } });
        recorder.failed.catch((error: unknown) => {
          if (live.recorder !== recorder) return;
          set({ problem: toProblem(error) });
          void finish('failed');
        });
        live.timers.push(
          window.setInterval(() => {
            if (live.recorder === recorder && outOfRoom(recorder.size, live.freeAtStart)) void get().stop('storage-full');
          }, 1000),
        );
      } catch (error) {
        floatingHost.close();
        stopStream(screen);
        mixer().remove('system');
        await live.session?.remove();
        live.session = null;
        set({ phase: { status: 'setup' }, screenStream: null, floating: false, problem: toProblem(error) });
      }
    },

    togglePause() {
      const { phase } = get();
      const recorder = live.recorder;
      if (!recorder) return;
      const now = performance.now();
      if (phase.status === 'recording') {
        recorder.pause();
        live.clock.pause(now);
        set({ phase: { status: 'paused' } });
      } else if (phase.status === 'paused') {
        recorder.resume();
        live.clock.resume(now);
        // What came before the pause is kept; a retake from here only drops what follows.
        live.takes.begin(recordingElapsed());
        set({ phase: { status: 'recording' } });
      }
    },

    async retake() {
      const recorder = live.recorder;
      if (!recorder || get().phase.status !== 'recording') return;
      const take = live.takes.take + 1;
      for (let remaining = COUNTDOWN_SECONDS; remaining > 0; remaining--) {
        set({ phase: { status: 'retaking', remaining, take } });
        await new Promise((resolve) => window.setTimeout(resolve, 1000));
        // Finished or discarded meanwhile.
        if (live.recorder !== recorder || get().phase.status !== 'retaking') return;
      }
      live.takes.retake(recordingElapsed());
      void live.session?.saveCuts(live.takes.cuts);
      set({ phase: { status: 'recording' } });
    },

    keepTake() {
      if (get().phase.status === 'retaking') set({ phase: { status: 'recording' } });
    },

    async stop(reason = 'user') {
      const status = get().phase.status;
      if (status === 'countdown') return get().discard();
      if (status !== 'recording' && status !== 'paused' && status !== 'retaking') return;
      await finish(reason);
    },

    async discard() {
      const status = get().phase.status;
      if (status === 'setup' || status === 'saving') return;
      const recorder = live.recorder;
      live.recorder = null;
      clearTimers();
      floatingHost.close();
      await recorder?.cancel();
      await live.session?.remove();
      live.session = null;
      stopStream(get().screenStream);
      live.mixer?.remove('system');
      set({ phase: { status: 'setup' }, screenStream: null, floating: false });
      // Back in setup: the previews keep running for another take.
      watchMicrophone();
    },

    async popOut() {
      if (!floatingHost.supported) return;
      set({ floating: await floatingHost.open() });
    },

    dismissProblem() {
      set({ problem: null });
    },

    async refreshLeftovers() {
      set({ leftovers: await findLeftovers().catch(() => []) });
    },

    async recover(id) {
      if (get().recovering) return;
      set({ recovering: { id, progress: 0 }, problem: null });
      const session = await RecordingSession.resume(id);
      if (!session) {
        set({ recovering: null });
        return void get().refreshLeftovers();
      }
      try {
        let prepared = await session.prepared();
        const raw = await session.raw();
        if (raw && raw.size > 0) {
          // The tab closed while recording or preparing: repair what is complete.
          const task = engine.prepareRecording(raw, { directory: session.path, name: session.name, salvage: true }, (progress) =>
            set({ recovering: { id, progress: progress.ratio } }),
          );
          prepared = await task.promise;
          await session.removeRaw();
        }
        if (!prepared) throw new Error('Nothing to recover');
        set({ recovering: null, leftovers: get().leftovers.filter((leftover) => leftover.id !== id) });
        await openInEditor(session, prepared.screen, prepared.camera);
      } catch {
        session.release();
        set({ recovering: null, problem: { kind: 'prepare-failed' } });
      }
    },

    async deleteLeftover(id) {
      const session = await RecordingSession.resume(id);
      await session?.remove();
      set({ leftovers: get().leftovers.filter((leftover) => leftover.id !== id) });
    },
  };
});

/** True while a recording is being made or saved (the home page gives way to the recording screen). */
export const selectRecordingActive = (state: RecordingStore): boolean => state.phase.status !== 'setup' && state.phase.status !== 'choosing';
